import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Stack, Group, Button, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { Plus, Upload, Users } from 'react-feather';
import type { Participant, CohortWithDetails } from '../../../types';
import { participantsApi } from '../../../services/api/participants';
import { ImportParticipantsModal } from './ImportParticipantsModal';
import { AddCustomColumnModal } from './AddCustomColumnModal';
import { AddParticipantModal } from './AddParticipantModal';
import { UploadMatchedGroupsModal } from './UploadMatchedGroupsModal';
import { GroupsView } from './GroupsView';
import { IntakeBanner } from './IntakeBanner';
import { notifications } from '@mantine/notifications';
import { ExportModal, EmptyState } from '../../../components/common';
import { ParticipantDetailModal } from './ParticipantDetailModal';
import { useParticipantsData, ParticipantsFilters, ParticipantsTableView } from './participants';
import { AttendanceFollowUpBanner } from './AttendanceFollowUpBanner';
import { PaperformDeletionsBanner } from './PaperformDeletionsBanner';
import { isPeerLedGroupCoaching } from '../../../utils/liveSessionDelivery';

interface ParticipantsTabProps {
  cohort: CohortWithDetails;
  viewMode: 'participants' | 'groups';
  groupsDisplayMode: 'list' | 'card';
  cardsPerRow?: number;
  registerHandlers?: (handlers: {
    openImportModal: () => void;
    openUploadGroupsModal: () => void;
    openAddGroupModal?: () => void;
  }) => void;
  isActive?: boolean;
  groupsSearchValue?: string;
  hasSessionDates?: boolean;
  hasTimeSlots?: boolean;
  onEditCohortDetails?: () => void;
  onParticipantsChanged?: () => void;
  autoOpenParticipantUserId?: string;
  defaultDetailTab?: string;
  onAutoOpenComplete?: () => void;
  /** Portal targets for rendering filter bar into the sub-header */
  filterBarPortalLeft?: HTMLElement | null;
  filterBarPortalRight?: HTMLElement | null;
  onOpenFollowUps?: (options?: { sessionId?: string; participantId?: string }) => void;
}

// The filter values are lower case; the Export modal shows them as the filter menu does.
const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export function ParticipantsTab({ cohort, viewMode, groupsDisplayMode, cardsPerRow, registerHandlers, isActive, groupsSearchValue, hasSessionDates, hasTimeSlots, onEditCohortDetails, onParticipantsChanged, autoOpenParticipantUserId, defaultDetailTab, onAutoOpenComplete, filterBarPortalLeft, filterBarPortalRight, onOpenFollowUps }: ParticipantsTabProps) {
  // ── Data hook (fetching, filtering, pagination, column config) ──
  const data = useParticipantsData({ cohortId: cohort.id, isActive, viewMode, groupsDisplayMode, groupsSearchValue });

  // MED-1161: peer-led cohorts (peer leaders, not coaches) must not surface
  // coach matching/assignment in the Participants & Groups area. Threaded down
  // to GroupsView → GroupCard / CoachesPanel / banners / CoachesSidePanel.
  // MED-1202: "has coaches or not" follows the coaching sub-type ALONE — a
  // peer-led cohort has no coaches whether or not Live Session Delivery is on.
  // Previously (MED-1161 AC10 / MED-1181) this was gated on LSD, so a peer-led
  // + LSD-off cohort wrongly showed coach surfaces. Coach-management visibility
  // is decoupled from LSD via the shared selector; the MED02 live/prep
  // experience stays LSD-gated elsewhere. See utils/liveSessionDelivery.ts.
  const isPeerLedCohort = isPeerLedGroupCoaching(cohort);

  const {
    participants,
    filteredParticipants,
    summary,
    displaySummary,
    totalCount,
    totalPages,
    loading,
    customFieldDefinitions,
    cohortGroups,
    loadingGroups,
    loadingMoreGroups,
    groupsPagination,
    loadMoreGroups,
    allParticipantsForGroups,
    groupOptions,
    personalityTypes,
    filters,
    columnConfig: colCfg,
    page,
    setPage,
    pageSize,
    setPageSize,
    formBaseUrls,
    fetchParticipants,
    fetchGroups,
    fetchAllParticipantsForGroups,
  } = data;

  // ── Modal state ──
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  const [importModalOpened, { open: openImportModal, close: closeImportModal }] = useDisclosure(false);
  const [detailModalOpened, { open: openDetailModal, close: closeDetailModal }] = useDisclosure(false);
  const [addColumnModalOpened, { open: openAddColumnModal, close: closeAddColumnModal }] = useDisclosure(false);
  const [addParticipantModalOpened, { open: openAddParticipantModal, close: closeAddParticipantModal }] = useDisclosure(false);
  const [uploadGroupsModalOpened, { open: openUploadGroupsModal, close: closeUploadGroupsModal }] = useDisclosure(false);
  const [exportModalOpened, { open: openExportModal, close: closeExportModal }] = useDisclosure(false);

  // Store GroupsView handlers for registration with parent
  const [groupsViewHandlers, setGroupsViewHandlers] = useState<{ openAddGroupModal: () => void } | null>(null);

  // Register handlers with parent for sub-header buttons
  useEffect(() => {
    if (registerHandlers) {
      registerHandlers({
        openImportModal,
        openUploadGroupsModal,
        ...(groupsViewHandlers && { openAddGroupModal: groupsViewHandlers.openAddGroupModal }),
      });
    }
  }, [registerHandlers, openImportModal, openUploadGroupsModal, groupsViewHandlers]);

  // ── Auto-open participant detail from URL params (Health Summary → View Participant) ──
  const pendingAutoOpenRef = useRef<string | null>(null);
  const [autoOpenDefaultTab, setAutoOpenDefaultTab] = useState<string | undefined>();

  // Capture auto-open params into ref when URL params arrive
  useEffect(() => {
    if (autoOpenParticipantUserId) {
      pendingAutoOpenRef.current = autoOpenParticipantUserId;
      setAutoOpenDefaultTab(defaultDetailTab || 'activity');
      // Ensure full participant list is loaded (paginated list may not contain target)
      fetchAllParticipantsForGroups();
      // Clear URL params immediately — ref preserves the target
      onAutoOpenComplete?.();
    }
  }, [autoOpenParticipantUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open participant detail when data finishes loading
  useEffect(() => {
    if (!pendingAutoOpenRef.current || loading) return;
    const targetUserId = pendingAutoOpenRef.current;
    // Search both paginated page AND full list (target may not be on current page)
    const participant =
      participants.find((p) => p.user_id === targetUserId) ||
      allParticipantsForGroups.find((p) => p.user_id === targetUserId);
    if (participant) {
      setSelectedParticipant(participant);
      openDetailModal();
      pendingAutoOpenRef.current = null;
    }
  }, [participants, allParticipantsForGroups, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Groups list enriched with coach names (consumed by the detail modal) ──
  const groupsForModal = useMemo(() => {
    const uniqueGroups = new Map<string, { id: string; name: string; coach_name?: string }>();
    participants.forEach((p) => {
      if (p.group_id && p.group_name) {
        const fullGroup = cohortGroups.find((g) => g.id === p.group_id);
        uniqueGroups.set(p.group_id, {
          id: p.group_id,
          name: p.group_name,
          coach_name: fullGroup?.coach_name || undefined,
        });
      }
    });
    return Array.from(uniqueGroups.values());
  }, [participants, cohortGroups]);

  // ── Row action handlers ──
  const handleViewParticipantByUserId = useCallback((userId: string) => {
    const participant = allParticipantsForGroups.find((p) => p.user_id === userId);
    if (participant) {
      setSelectedParticipant(participant);
      openDetailModal();
    }
  }, [allParticipantsForGroups, openDetailModal]);

  const handleRowClick = (participant: Participant) => {
    setSelectedParticipant(participant);
    openDetailModal();
  };

  const handleWithdrawParticipant = (participant: Participant) => {
    modals.openConfirmModal({
      title: 'Withdraw Participant',
      children: (
        <Stack gap="sm">
          <Text size="sm">
            Are you sure you want to withdraw <strong>{participant.first_name} {participant.last_name}</strong> from this cohort?
          </Text>
          <Text size="sm">This will:</Text>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li><Text size="sm">Remove them from future session lists</Text></li>
            <li><Text size="sm">Cancel any scheduled emails for them</Text></li>
            <li><Text size="sm">Exclude them from RSVP counts</Text></li>
          </ul>
          <Text size="sm" c="dimmed">
            Their existing feedback and attendance history will be preserved.
          </Text>
        </Stack>
      ),
      labels: { confirm: 'Withdraw', cancel: 'Cancel' },
      confirmProps: { color: 'orange' },
      onConfirm: async () => {
        try {
          await participantsApi.withdraw(cohort.id, participant.id);
          notifications.show({
            title: 'Participant Withdrawn',
            message: `${participant.first_name} ${participant.last_name} has been withdrawn from the cohort`,
            color: 'green',
          });
          fetchParticipants();
        } catch (error) {
          notifications.show({
            title: 'Error',
            message: error instanceof Error ? error.message : 'Failed to withdraw participant',
            color: 'red',
          });
        }
      },
    });
  };

  const handleReactivateParticipant = (participant: Participant) => {
    modals.openConfirmModal({
      title: 'Re-activate Participant',
      children: (
        <Text size="sm">
          Are you sure you want to re-activate <strong>{participant.first_name} {participant.last_name}</strong> in this cohort?
          They will be added back to future session lists and resume receiving communications.
        </Text>
      ),
      labels: { confirm: 'Re-activate', cancel: 'Cancel' },
      confirmProps: { color: 'medleyGreen' },
      onConfirm: async () => {
        try {
          await participantsApi.reactivate(cohort.id, participant.id);
          notifications.show({
            title: 'Participant Re-activated',
            message: `${participant.first_name} ${participant.last_name} has been re-activated in the cohort`,
            color: 'green',
          });
          fetchParticipants();
        } catch (error) {
          notifications.show({
            title: 'Error',
            message: error instanceof Error ? error.message : 'Failed to re-activate participant',
            color: 'red',
          });
        }
      },
    });
  };

  // ── Modal close handlers ──
  const handleParticipantUpdate = () => {
    fetchParticipants();
    if (selectedParticipant) {
      const updated = participants.find((p) => p.id === selectedParticipant.id);
      if (updated) {
        setSelectedParticipant(updated);
      }
    }
  };

  const handleParticipantDelete = () => {
    setSelectedParticipant(null);
    fetchParticipants();
  };

  // ── Empty state ──
  if (!loading && displaySummary.total === 0 && !filters.hasActiveFilters) {
    return (
      <>
        <EmptyState
          icon={<Users size={40} />}
          title="No Participants Yet"
          description="Import participants from a CSV file or add them manually to get started."
          action={
            <Group gap="md">
              <Button leftSection={<Upload size={18} />} onClick={openImportModal}>
                Import from CSV
              </Button>
              <Button variant="default" leftSection={<Plus size={18} />} onClick={openAddParticipantModal}>
                Add Manually
              </Button>
            </Group>
          }
          size="lg"
          withCard
        />

        <ImportParticipantsModal
          opened={importModalOpened}
          onClose={closeImportModal}
          cohortId={cohort.id}
          cohortName={cohort.name}
          partnerName={cohort.partner_name}
          onImportComplete={() => {
            closeImportModal();
            fetchParticipants();
            onParticipantsChanged?.();
          }}
          existingParticipantCount={summary?.total ?? 0}
        />

        <AddParticipantModal
          opened={addParticipantModalOpened}
          onClose={closeAddParticipantModal}
          cohortId={cohort.id}
          partnerName={cohort.partner_name}
          customFieldDefinitions={customFieldDefinitions}
          onParticipantAdded={() => {
            closeAddParticipantModal();
            fetchParticipants();
            onParticipantsChanged?.();
          }}
        />
      </>
    );
  }

  // ── Main render ──
  return (
    <Stack gap="lg">
      {/* Intake Banner - only shown for participants view */}
      {viewMode === 'participants' && (
        <IntakeBanner
          cohortId={cohort.id}
          cohortName={cohort.name}
          onIntakeSent={fetchParticipants}
          participantFeaturesEnabled={cohort.participant_features_enabled}
        />
      )}

      {/* E19-6: Attendance Follow-Up Banner */}
      {viewMode === 'participants' && onOpenFollowUps && (
        <AttendanceFollowUpBanner cohortId={cohort.id} onOpenFollowUps={onOpenFollowUps} />
      )}

      {/* MED-903: Paperform deletion detection banner (Participants tab only) */}
      {viewMode === 'participants' && (
        <PaperformDeletionsBanner cohortId={cohort.id} />
      )}

      {/* Filter Bar - only shown for participants view */}
      {viewMode === 'participants' && (
        <ParticipantsFilters
          filters={filters}
          groupOptions={groupOptions}
          personalityTypes={personalityTypes}
          columnConfig={colCfg.columns}
          onColumnsChange={colCfg.setColumns}
          onAddCustomColumn={openAddColumnModal}
          onDeleteCustomColumn={colCfg.handleDeleteCustomColumn}
          onExport={openExportModal}
          onAddParticipant={openAddParticipantModal}
          onUploadCSV={openImportModal}
          portalTargetLeft={filterBarPortalLeft}
          portalTargetRight={filterBarPortalRight}
        />
      )}

      {/* Participants Table - shown when viewMode is 'participants' */}
      {viewMode === 'participants' && (
        <ParticipantsTableView
          loading={loading}
          participants={filteredParticipants}
          columnConfig={colCfg.columns}
          sortConfig={colCfg.sortConfig}
          onSort={colCfg.handleSort}
          visibleColumnCount={colCfg.visibleColumnCount}
          hasActiveFilters={filters.hasActiveFilters}
          onClearFilters={filters.clearFilters}
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onRowClick={handleRowClick}
          formBaseUrls={formBaseUrls}
        />
      )}

      {/* Groups View - shown when viewMode is 'groups' */}
      {viewMode === 'groups' && (
        <GroupsView
          cohortId={cohort.id}
          groups={cohortGroups}
          participants={allParticipantsForGroups}
          loading={loadingGroups}
          loadingMore={loadingMoreGroups}
          groupsPagination={groupsPagination}
          onLoadMore={loadMoreGroups}
          displayMode={groupsDisplayMode}
          cardsPerRow={cardsPerRow}
          onUploadGroups={openUploadGroupsModal}
          onRefresh={async () => {
            await Promise.all([fetchParticipants(), fetchGroups(), fetchAllParticipantsForGroups()]);
          }}
          registerHandlers={setGroupsViewHandlers}
          onViewParticipant={handleViewParticipantByUserId}
          searchValue={groupsSearchValue}
          groupsPrereqsMet={hasSessionDates !== false && hasTimeSlots !== false}
          hasSessionDates={hasSessionDates}
          hasTimeSlots={hasTimeSlots}
          onEditCohortDetails={onEditCohortDetails}
          hasOneOnOneModality={cohort.modalities?.some((m) => m.modality_type === 'one_on_one') ?? false}
          coachFeaturesEnabled={cohort.coach_features_enabled}
          isPeerLed={isPeerLedCohort}
          configuredGroupCoachingSessionCount={
            cohort.modalities?.find((m) => m.modality_type === 'group_coaching')?.session_count ?? null
          }
        />
      )}

      {/* Import Modal */}
      <ImportParticipantsModal
        opened={importModalOpened}
        onClose={closeImportModal}
        cohortId={cohort.id}
        cohortName={cohort.name}
        partnerName={cohort.partner_name}
        onImportComplete={() => {
          closeImportModal();
          fetchParticipants();
          onParticipantsChanged?.();
        }}
        existingParticipantCount={summary?.total ?? 0}
      />

      {/* Add Custom Column Modal */}
      <AddCustomColumnModal
        opened={addColumnModalOpened}
        onClose={closeAddColumnModal}
        cohortId={cohort.id}
        participants={participants}
        onFieldCreated={() => {
          closeAddColumnModal();
          fetchParticipants();
        }}
      />

      {/* Add Participant Modal */}
      <AddParticipantModal
        opened={addParticipantModalOpened}
        onClose={closeAddParticipantModal}
        cohortId={cohort.id}
        partnerName={cohort.partner_name}
        customFieldDefinitions={customFieldDefinitions}
        onParticipantAdded={() => {
          closeAddParticipantModal();
          fetchParticipants();
          onParticipantsChanged?.();
        }}
      />

      {/* Participant Detail Modal */}
      <ParticipantDetailModal
        opened={detailModalOpened}
        onClose={() => {
          closeDetailModal();
          setSelectedParticipant(null);
          setAutoOpenDefaultTab(undefined);
        }}
        participant={selectedParticipant}
        cohortId={cohort.id}
        cohortStatus={cohort.status}
        groups={groupsForModal}
        customFieldDefinitions={customFieldDefinitions}
        onUpdate={handleParticipantUpdate}
        onDelete={handleParticipantDelete}
        onWithdraw={selectedParticipant ? () => handleWithdrawParticipant(selectedParticipant) : undefined}
        onReactivate={selectedParticipant ? () => handleReactivateParticipant(selectedParticipant) : undefined}
        defaultTab={autoOpenDefaultTab || defaultDetailTab}
      />

      {/* Upload Matched Groups Modal */}
      <UploadMatchedGroupsModal
        opened={uploadGroupsModalOpened}
        onClose={closeUploadGroupsModal}
        cohortId={cohort.id}
        onImportComplete={() => {
          closeUploadGroupsModal();
          fetchGroups();
          fetchParticipants();
        }}
        hasSessionDates={hasSessionDates}
        hasTimeSlots={hasTimeSlots}
        onEditCohortDetails={onEditCohortDetails}
      />

      {/* Export Modal */}
      <ExportModal
        opened={exportModalOpened}
        onClose={closeExportModal}
        entityType="participants"
        contextType="cohort"
        contextId={cohort.id}
        recordCount={totalCount}
        filterContext={{
          search: filters.debouncedSearch || undefined,
          status: filters.statusFilter !== 'all' ? filters.statusFilter : undefined,
          groupIds: filters.groupFilter !== 'all' ? [filters.groupFilter] : undefined,
          assignedStatus: filters.assignedFilter !== 'all' ? filters.assignedFilter : undefined,
          personalityType: filters.personalityFilter !== 'all' ? filters.personalityFilter : undefined,
        }}
        filterSummary={[
          ...(filters.debouncedSearch ? [{ label: 'Search', value: filters.debouncedSearch }] : []),
          ...(filters.statusFilter !== 'all' ? [{ label: 'Status', value: capitalize(filters.statusFilter) }] : []),
          ...(filters.groupFilter !== 'all'
            ? [{ label: 'Group', value: groupOptions.find(g => g.value === filters.groupFilter)?.label ?? filters.groupFilter }]
            : []),
          ...(filters.assignedFilter !== 'all' ? [{ label: 'Assignment', value: capitalize(filters.assignedFilter) }] : []),
          ...(filters.personalityFilter !== 'all' ? [{ label: 'Type', value: filters.personalityFilter }] : []),
        ]}
        // MDF-208: the export does not apply these screen filters.
        ignoredFilters={[
          ...(filters.intakeFilter !== 'all' ? ['Intake'] : []),
          ...(filters.flaggedOnly ? ['Show flagged only'] : []),
        ]}
      />
    </Stack>
  );
}
